import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Icon, ActivityIndicator } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    loadJobCostHistoryLive,
    loadARServiceBillingConsolidated,
    loadBillablePhases,
    fNum,
    pNum
} from '../db/company';
import { selectedTeamAtom, selectedJobAtom, dataSourceJobsAtom } from '../store/company';
import moment from 'moment';
import xlsx from 'xlsx';
import TimeMaterialsReportRolling9 from './timeMaterialsReportRolling9';

// ---------------------------------------------------------------------------
// Period definitions — mirrors the example workbook (T&M Report Example).
// Weekly Trending: Last Week, 2 Weeks Ago, 3 Weeks Ago, Last Month, JTD
// ---------------------------------------------------------------------------
const computePeriods = (now = moment()) => {
    const lastWeekEnd = now.clone().startOf('isoWeek').subtract(1, 'day');         // Sunday
    const lastWeekStart = lastWeekEnd.clone().subtract(6, 'day');                   // Monday
    const twoWeekEnd = lastWeekStart.clone().subtract(1, 'day');
    const twoWeekStart = twoWeekEnd.clone().subtract(6, 'day');
    const threeWeekEnd = twoWeekStart.clone().subtract(1, 'day');
    const threeWeekStart = threeWeekEnd.clone().subtract(6, 'day');
    const lastMonthStart = now.clone().subtract(1, 'month').startOf('month');
    const lastMonthEnd = now.clone().subtract(1, 'month').endOf('month');

    const mk = (key, label, start, end) => ({
        key,
        label,
        start: start ? start.clone() : null,
        end: end ? end.clone() : null,
        contains: (d) => {
            if (!d) return false;
            if (start && d.isBefore(start, 'day')) return false;
            if (end && d.isAfter(end, 'day')) return false;
            return true;
        }
    });

    return {
        lastWeek: mk('lastWeek', 'Last Week', lastWeekStart, lastWeekEnd),
        twoWeeks: mk('twoWeeks', '2 Weeks Ago', twoWeekStart, twoWeekEnd),
        threeWeeks: mk('threeWeeks', '3 Weeks Ago', threeWeekStart, threeWeekEnd),
        lastMonth: mk('lastMonth', 'Last Month', lastMonthStart, lastMonthEnd),
        jtd: mk('jtd', 'JTD', null, null)
    };
};

// Cost-type code → category (matches the buckets the old report used).
const categoryForCostType = (code) => {
    if (!code) return null;
    const c = String(code);
    if (c.startsWith('51')) return 'labor';
    if (c.startsWith('52')) return 'equipment';
    if (c.startsWith('53')) return 'materials';
    if (c.startsWith('54')) return 'subs';
    if (c.startsWith('55') || c.startsWith('56') || c.startsWith('57')) return 'other';
    return null;
};

const CATEGORIES = [
    { key: 'labor', label: 'Labor' },
    { key: 'equipment', label: 'Equipment' },
    { key: 'materials', label: 'Materials' },
    { key: 'subs', label: 'Subs' },
    { key: 'other', label: 'Other' }
];

// Column names confirmed against v_AR_Service_Billing_Detail_Consolidated.
// `Amount` is the primary line amount (works for both active and history records);
// fall back to Invoice_Amount / Revenue_Amount / Extended_Price_Amount if blank.
const getBillingDate = (row) => {
    const v = row?.GL_Date || row?.Invoice_Date || row?.Service_Date;
    return v ? moment(v) : null;
};

const getBillingAmount = (row) => {
    if (!row) return 0;
    return pNum(row.Amount) || pNum(row.Invoice_Amount) || pNum(row.Revenue_Amount) || pNum(row.Extended_Price_Amount);
};

const getBillingCostType = (row) => row?.Cost_Type_Code || null;

const getBillingJobNumber = (row) => row?.BU_ID || null;

const getBillingTicket = (row) => row?.JDE_Ticket_Number || row?.Ticket_Number || null;

// AR Service Billing carries JDE_Ticket_Number but not JDE_Timecard_Number —
// labor lines on the cost side that only have a timecard can't be matched here.
const getBillingTimecard = () => null;

const getBillingInvoiced = (row) => !!(row?.Invoice_Number && row?.Invoice_Date);

// Bucket cost rows by period × category. Returns:
//   { [periodKey]: { [category]: number, total: number } }
// `filter` decides whether each row belongs (e.g. billable-only / non-billable-only).
const bucketCosts = (costRows, periods, billableLookup, filter) => {
    const out = {};
    Object.keys(periods).forEach(k => {
        out[k] = { labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, total: 0 };
    });
    (costRows || []).forEach(row => {
        const cat = categoryForCostType(row.Cost_Type_Code);
        if (!cat) return;
        const isBillable = !!billableLookup?.[row.Job_Number]?.[row.Phase_Code];
        if (!filter(isBillable, row)) return;
        const cost = pNum(row.Job_Cost);
        const d = row.GL_Date ? moment(row.GL_Date) : null;
        Object.entries(periods).forEach(([k, p]) => {
            if (p.contains(d)) {
                out[k][cat] += cost;
                out[k].total += cost;
            }
        });
    });
    return out;
};

const bucketBillings = (billingRows, periods, filter) => {
    const out = {};
    Object.keys(periods).forEach(k => {
        out[k] = { labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, total: 0 };
    });
    (billingRows || []).forEach(row => {
        if (filter && !filter(row)) return;
        const cat = categoryForCostType(getBillingCostType(row)) || 'other';
        const amt = getBillingAmount(row);
        const d = getBillingDate(row);
        Object.entries(periods).forEach(([k, p]) => {
            if (p.contains(d)) {
                out[k][cat] += amt;
                out[k].total += amt;
            }
        });
    });
    return out;
};

// Per-ticket breakdown for Weekly Trending expansion. Returns:
//   { [cat]: [ { ticket, periods: { [pk]: { rev, cost } } } ] }
// `mode` chooses which phase classification to include:
//   'billable'    → only billable phases
//   'nonBillable' → only non-billable phases
//   'total'       → both
const bucketByTicket = (costRows, billingRows, periods, billableLookup, mode) => {
    const wantBillable = (isBillable) => {
        if (mode === 'billable') return isBillable;
        if (mode === 'nonBillable') return !isBillable;
        return true;
    };
    const buckets = {};
    CATEGORIES.forEach(c => { buckets[c.key] = new Map(); });
    const ensure = (cat, ticket) => {
        if (!buckets[cat].has(ticket)) {
            const periodsObj = {};
            Object.keys(periods).forEach(pk => { periodsObj[pk] = { rev: 0, cost: 0 }; });
            buckets[cat].set(ticket, { ticket, periods: periodsObj });
        }
        return buckets[cat].get(ticket);
    };

    (costRows || []).forEach(row => {
        const cat = categoryForCostType(row.Cost_Type_Code);
        if (!cat) return;
        const isBillable = !!billableLookup?.[row.Job_Number]?.[row.Phase_Code];
        if (!wantBillable(isBillable)) return;
        const ticket = row.JDE_Ticket_Number || '(no ticket)';
        const d = row.GL_Date ? moment(row.GL_Date) : null;
        const c = pNum(row.Job_Cost);
        Object.entries(periods).forEach(([pk, p]) => {
            if (p.contains(d)) ensure(cat, ticket).periods[pk].cost += c;
        });
    });

    (billingRows || []).forEach(row => {
        const cat = categoryForCostType(getBillingCostType(row)) || 'other';
        const job = getBillingJobNumber(row);
        const isBillable = !!billableLookup?.[job]?.[row?.Phase_Code];
        if (!wantBillable(isBillable)) return;
        const ticket = getBillingTicket(row) || '(no ticket)';
        const d = getBillingDate(row);
        const amt = getBillingAmount(row);
        Object.entries(periods).forEach(([pk, p]) => {
            if (p.contains(d)) ensure(cat, ticket).periods[pk].rev += amt;
        });
    });

    const result = {};
    Object.keys(buckets).forEach(cat => {
        result[cat] = Array.from(buckets[cat].values())
            .filter(t => Object.values(t.periods).some(p => p.rev || p.cost))
            .sort((a, b) => {
                const sumA = Object.values(a.periods).reduce((s, p) => s + p.rev + p.cost, 0);
                const sumB = Object.values(b.periods).reduce((s, p) => s + p.rev + p.cost, 0);
                return sumB - sumA;
            });
    });
    return result;
};

// Per-classification breakdown for Weekly Trending expansion. Cost side only —
// AR Service Billing lines carry no classification (Line_Item_Description is
// blank), so revenue can't be split this way. Returns:
//   { [cat]: [ { name, periods: { [pk]: { cost } } } ] }
// Labor rows classify by craft (Craftsman, Foreman (Hourly), Welder...),
// equipment by tier (Truck - 3/4 Ton...), everything else by cost-type
// description — all synthesized server-side into row.Classification.
const bucketByClassification = (costRows, periods, billableLookup, mode) => {
    const wantBillable = (isBillable) => {
        if (mode === 'billable') return isBillable;
        if (mode === 'nonBillable') return !isBillable;
        return true;
    };
    const buckets = {};
    CATEGORIES.forEach(c => { buckets[c.key] = new Map(); });
    const ensure = (cat, name) => {
        if (!buckets[cat].has(name)) {
            const periodsObj = {};
            Object.keys(periods).forEach(pk => { periodsObj[pk] = { cost: 0 }; });
            buckets[cat].set(name, { name, periods: periodsObj });
        }
        return buckets[cat].get(name);
    };

    (costRows || []).forEach(row => {
        const cat = categoryForCostType(row.Cost_Type_Code);
        if (!cat) return;
        const isBillable = !!billableLookup?.[row.Job_Number]?.[row.Phase_Code];
        if (!wantBillable(isBillable)) return;
        const name = row.Classification || '(unclassified)';
        const d = row.GL_Date ? moment(row.GL_Date) : null;
        const c = pNum(row.Job_Cost);
        Object.entries(periods).forEach(([pk, p]) => {
            if (p.contains(d)) ensure(cat, name).periods[pk].cost += c;
        });
    });

    const result = {};
    Object.keys(buckets).forEach(cat => {
        result[cat] = Array.from(buckets[cat].values())
            .filter(t => Object.values(t.periods).some(p => p.cost))
            .sort((a, b) => {
                const sumA = Object.values(a.periods).reduce((s, p) => s + p.cost, 0);
                const sumB = Object.values(b.periods).reduce((s, p) => s + p.cost, 0);
                return sumB - sumA;
            });
    });
    return result;
};

// Sum two { [periodKey]: { [cat]: number, total: number } } maps element-wise.
const sumPeriodCatMaps = (a, b) => {
    const out = {};
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    keys.forEach(pk => {
        out[pk] = { labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, total: 0 };
        Object.keys(out[pk]).forEach(cat => {
            out[pk][cat] = (a?.[pk]?.[cat] || 0) + (b?.[pk]?.[cat] || 0);
        });
    });
    return out;
};

// "Left to Bill" reconciliation: per-ticket breakdown of unreconciled rev/cost
// across the LLEEMMSSOO categories. A ticket lands here when:
//   - it has cost rows but no matching billing ticket (cost columns populated)
//   - it has billing rows but no matching cost ticket (rev columns populated)
const reconcileTickets = (costRows, billingRows, billableLookup) => {
    const map = new Map();
    const ensure = (key) => {
        if (!map.has(key)) {
            map.set(key, {
                key,
                ticket: '',
                job: '',
                status: '',
                latestDate: null,
                source: null, // 'cost' or 'billing'
                labor: { rev: 0, cost: 0 },
                equipment: { rev: 0, cost: 0 },
                materials: { rev: 0, cost: 0 },
                subs: { rev: 0, cost: 0 },
                other: { rev: 0, cost: 0 }
            });
        }
        return map.get(key);
    };

    const billingTicketSet = new Set();
    (billingRows || []).forEach(row => {
        const job = getBillingJobNumber(row);
        const tk = getBillingTicket(row);
        if (job && tk) billingTicketSet.add(`${job}|${tk}`);
    });
    const costTicketSet = new Set();
    (costRows || []).forEach(row => {
        const tk = row.JDE_Ticket_Number;
        if (row.Job_Number && tk) costTicketSet.add(`${row.Job_Number}|${tk}`);
    });

    // Cost rows w/ no matching billing ticket
    (costRows || []).forEach(row => {
        const cat = categoryForCostType(row.Cost_Type_Code);
        if (!cat) return;
        const isBillable = !!billableLookup?.[row.Job_Number]?.[row.Phase_Code];
        if (!isBillable) return;
        const tk = row.JDE_Ticket_Number;
        if (!tk) return;
        const key = `${row.Job_Number}|${tk}`;
        if (billingTicketSet.has(key)) return;
        const entry = ensure(key);
        entry.ticket = tk;
        entry.job = row.Job_Number;
        entry.source = entry.source || 'cost';
        entry[cat].cost += pNum(row.Job_Cost);
        const d = row.GL_Date ? moment(row.GL_Date) : null;
        if (d && (!entry.latestDate || d.isAfter(entry.latestDate))) entry.latestDate = d;
    });

    // Billing rows w/ no matching cost ticket
    (billingRows || []).forEach(row => {
        const job = getBillingJobNumber(row);
        const tk = getBillingTicket(row);
        if (!tk) return;
        const key = `${job}|${tk}`;
        if (costTicketSet.has(key)) return;
        const cat = categoryForCostType(getBillingCostType(row)) || 'other';
        const entry = ensure(key);
        entry.ticket = tk;
        entry.job = job;
        entry.source = entry.source || 'billing';
        if (!entry.status && row?.Ticket_Status) entry.status = row.Ticket_Status;
        entry[cat].rev += getBillingAmount(row);
        const d = getBillingDate(row);
        if (d && (!entry.latestDate || d.isAfter(entry.latestDate))) entry.latestDate = d;
    });

    return Array.from(map.values()).sort((a, b) => {
        if (a.latestDate && b.latestDate) return b.latestDate.valueOf() - a.latestDate.valueOf();
        if (a.latestDate) return -1;
        if (b.latestDate) return 1;
        return String(a.ticket).localeCompare(String(b.ticket));
    });
};

// ---------------------------------------------------------------------------
// Cell + table rendering helpers
// ---------------------------------------------------------------------------
const s = StyleSheet.create({
    cell: {
        height: 22,
        border: '1px solid #e2e8f0',
        padding: '2px 6px',
        fontSize: 11,
        textAlign: 'right',
        color: '#0f172a',
        whiteSpace: 'nowrap'
    },
    cellLeft: {
        height: 22,
        border: '1px solid #e2e8f0',
        padding: '2px 6px',
        fontSize: 11,
        textAlign: 'left',
        color: '#0f172a',
        whiteSpace: 'nowrap'
    },
    head: {
        height: 24,
        border: '1px solid #cbd5e1',
        padding: '2px 6px',
        fontSize: 11,
        fontWeight: 600,
        textAlign: 'center',
        backgroundColor: '#f1f5f9',
        color: '#0f172a'
    },
    sectionTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 12, marginBottom: 6 },
    viewBtn: (active) => ({
        padding: '8px 14px',
        borderRadius: 4,
        border: active ? '1px solid #2563eb' : '1px solid #cbd5e1',
        backgroundColor: active ? '#dbeafe' : '#fff',
        marginRight: 8,
        cursor: 'pointer'
    })
});

const margin = (rev, cost) => {
    const profit = rev - cost;
    const m = rev ? (profit / rev * 100) : 0;
    return { profit, m };
};

const Quad = ({ rev, cost }) => {
    const { profit, m } = margin(rev, cost);
    return (
        <View style={{ flexDirection: 'row' }}>
            <Text style={[s.cell, { width: 80 }]}>{rev ? fNum(rev, 1) : ''}</Text>
            <Text style={[s.cell, { width: 80 }]}>{cost ? fNum(cost, 1) : ''}</Text>
            <Text style={[s.cell, { width: 80, fontWeight: 600 }]}>{profit ? fNum(profit, 1) : ''}</Text>
            <Text style={[
                s.cell,
                {
                    width: 70,
                    backgroundColor: m > 40 ? '#dcfce7' : (m < 10 && rev ? '#fee2e2' : 'transparent')
                }
            ]}>{rev ? m.toFixed(1) + '%' : ''}</Text>
        </View>
    );
};

const PeriodHeader = ({ periodLabels }) => (
    <View>
        <View style={{ flexDirection: 'row' }}>
            <Text style={[s.head, { width: 200 }]}></Text>
            {periodLabels.map((label, i) => (
                <Text key={i} style={[s.head, { width: 80 * 3 + 70 }]}>{label}</Text>
            ))}
        </View>
        <View style={{ flexDirection: 'row' }}>
            <Text style={[s.head, { width: 200 }]}>Category</Text>
            {periodLabels.map((_, i) => (
                <View key={i} style={{ flexDirection: 'row' }}>
                    <Text style={[s.head, { width: 80 }]}>Rev</Text>
                    <Text style={[s.head, { width: 80 }]}>Cost</Text>
                    <Text style={[s.head, { width: 80 }]}>Profit</Text>
                    <Text style={[s.head, { width: 70 }]}>Margin</Text>
                </View>
            ))}
        </View>
    </View>
);

// Cost-only detail row cells — classification detail has no revenue side
// (billing lines carry no classification), so Rev/Profit/Margin stay blank.
const CostQuad = ({ cost }) => (
    <View style={{ flexDirection: 'row' }}>
        <Text style={[s.cell, { width: 80 }]}></Text>
        <Text style={[s.cell, { width: 80 }]}>{cost ? fNum(cost, 1) : ''}</Text>
        <Text style={[s.cell, { width: 80 }]}></Text>
        <Text style={[s.cell, { width: 70 }]}></Text>
    </View>
);

const DETAIL_MODES = [
    { key: 'class', label: 'Classification' },
    { key: 'ticket', label: 'Ticket' }
];

const AnalysisTable = ({ periodKeys, periodLabels, revByPeriod, costByPeriod, ticketsByCategory, classesByCategory }) => {
    const [expanded, setExpanded] = useState(() => new Set());
    const [detailMode, setDetailMode] = useState('class');
    const toggle = (cat) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat);
            else next.add(cat);
            return next;
        });
    };

    const renderCatRow = (catKey, label) => {
        const tickets = ticketsByCategory?.[catKey] || [];
        const classes = classesByCategory?.[catKey] || [];
        const detail = detailMode === 'class' ? classes : tickets;
        const isExpandable = catKey !== 'total' && detail.length > 0;
        const isExpanded = expanded.has(catKey);
        return (
            <View key={catKey}>
                <View style={{ flexDirection: 'row' }}>
                    {isExpandable ? (
                        <Pressable onPress={() => toggle(catKey)} style={{ flexDirection: 'row' }}>
                            <Text style={[s.cellLeft, { width: 200, fontWeight: 400, cursor: 'pointer' }]}>
                                <Text style={{ color: '#64748b', fontSize: 10 }}>{isExpanded ? '▼' : '▶'}</Text>
                                {'  '}{label}
                            </Text>
                        </Pressable>
                    ) : (
                        <Text style={[s.cellLeft, { width: 200, fontWeight: catKey === 'total' ? 700 : 400 }]}>{label}</Text>
                    )}
                    {periodKeys.map(pk => {
                        const rev = revByPeriod?.[pk]?.[catKey] ?? 0;
                        const cost = costByPeriod?.[pk]?.[catKey] ?? 0;
                        return <Quad key={pk} rev={rev} cost={cost} />;
                    })}
                </View>
                {isExpanded && detailMode === 'class' && classes.map((cl, i) => (
                    <View key={`${catKey}-${cl.name}-${i}`} style={{ flexDirection: 'row', backgroundColor: '#f8fafc' }}>
                        <Text style={[s.cellLeft, { width: 200, paddingLeft: 28, fontSize: 10, color: '#475569' }]}>{cl.name}</Text>
                        {periodKeys.map(pk => (
                            <CostQuad key={pk} cost={cl.periods?.[pk]?.cost || 0} />
                        ))}
                    </View>
                ))}
                {isExpanded && detailMode === 'ticket' && tickets.map((t, i) => (
                    <View key={`${catKey}-${t.ticket}-${i}`} style={{ flexDirection: 'row', backgroundColor: '#f8fafc' }}>
                        <Text style={[s.cellLeft, { width: 200, paddingLeft: 28, fontSize: 10, color: '#475569' }]}>{t.ticket}</Text>
                        {periodKeys.map(pk => {
                            const rev = t.periods?.[pk]?.rev || 0;
                            const cost = t.periods?.[pk]?.cost || 0;
                            return <Quad key={pk} rev={rev} cost={cost} />;
                        })}
                    </View>
                ))}
            </View>
        );
    };

    return (
        <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                <Text style={{ fontSize: 10, color: '#64748b', marginRight: 6 }}>Expand by:</Text>
                {DETAIL_MODES.map(m => (
                    <Pressable
                        key={m.key}
                        onPress={() => setDetailMode(m.key)}
                        style={{
                            padding: '2px 8px',
                            marginRight: 4,
                            borderRadius: 3,
                            border: detailMode === m.key ? '1px solid #2563eb' : '1px solid #cbd5e1',
                            backgroundColor: detailMode === m.key ? '#dbeafe' : '#fff',
                            cursor: 'pointer'
                        }}
                    >
                        <Text style={{ fontSize: 10, color: '#0f172a' }}>{m.label}</Text>
                    </Pressable>
                ))}
                {detailMode === 'class' ? (
                    <Text style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>
                        Classification detail is cost-only — billing lines carry no classification.
                    </Text>
                ) : null}
            </View>
            <PeriodHeader periodLabels={periodLabels} />
            {CATEGORIES.map(c => renderCatRow(c.key, c.label))}
            {renderCatRow('total', 'Total')}
        </View>
    );
};

// Per-ticket Left-to-Bill table.
// Rows: ticket numbers. Columns: ticket # / date / [Labor Rev | Labor Cost | ...]
// per LLEEMMSSOO category, then Total Rev / Total Cost. Totals row at the end.
const ReconcileTable = ({ tickets }) => {
    const tkW = 130;
    const stW = 110;
    const dtW = 90;
    const catW = 80; // per rev/cost column
    const totW = 90; // per total column

    const totals = {
        labor: { rev: 0, cost: 0 },
        equipment: { rev: 0, cost: 0 },
        materials: { rev: 0, cost: 0 },
        subs: { rev: 0, cost: 0 },
        other: { rev: 0, cost: 0 }
    };
    let grandRev = 0;
    let grandCost = 0;
    (tickets || []).forEach(t => {
        CATEGORIES.forEach(c => {
            totals[c.key].rev += t[c.key]?.rev || 0;
            totals[c.key].cost += t[c.key]?.cost || 0;
            grandRev += t[c.key]?.rev || 0;
            grandCost += t[c.key]?.cost || 0;
        });
    });

    return (
        <View>
            <Text style={s.sectionTitle}>Left to Bill — by Ticket</Text>
            <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                Tickets with cost but no matching billing (Cost columns), or billing with no matching cost (Rev columns).
            </Text>

            {/* Header row 1: category labels spanning Rev+Cost */}
            <View style={{ flexDirection: 'row' }}>
                <Text style={[s.head, { width: tkW }]}></Text>
                <Text style={[s.head, { width: stW }]}></Text>
                <Text style={[s.head, { width: dtW }]}></Text>
                {CATEGORIES.map(c => (
                    <Text key={c.key} style={[s.head, { width: catW * 2 }]}>{c.label}</Text>
                ))}
                <Text style={[s.head, { width: totW * 2 }]}>Total</Text>
            </View>
            {/* Header row 2: Ticket / Status / Date / Rev/Cost pairs */}
            <View style={{ flexDirection: 'row' }}>
                <Text style={[s.head, { width: tkW }]}>Ticket #</Text>
                <Text style={[s.head, { width: stW }]}>Ticket Status</Text>
                <Text style={[s.head, { width: dtW }]}>Date</Text>
                {CATEGORIES.map(c => (
                    <View key={c.key} style={{ flexDirection: 'row' }}>
                        <Text style={[s.head, { width: catW }]}>Rev</Text>
                        <Text style={[s.head, { width: catW }]}>Cost</Text>
                    </View>
                ))}
                <Text style={[s.head, { width: totW }]}>Rev</Text>
                <Text style={[s.head, { width: totW }]}>Cost</Text>
            </View>

            {(tickets || []).length === 0 ? (
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s.cellLeft, { width: tkW + stW + dtW + catW * 10 + totW * 2, fontStyle: 'italic', color: '#94a3b8' }]}>
                        No unreconciled tickets.
                    </Text>
                </View>
            ) : (tickets || []).map(t => {
                let rowRev = 0;
                let rowCost = 0;
                CATEGORIES.forEach(c => {
                    rowRev += t[c.key]?.rev || 0;
                    rowCost += t[c.key]?.cost || 0;
                });
                return (
                    <View key={t.key} style={{ flexDirection: 'row' }}>
                        <Text style={[s.cellLeft, { width: tkW }]}>{t.ticket}</Text>
                        <Text style={[s.cellLeft, { width: stW }]}>{t.status || ''}</Text>
                        <Text style={[s.cellLeft, { width: dtW }]}>{t.latestDate ? t.latestDate.format('M/D/YY') : ''}</Text>
                        {CATEGORIES.map(c => (
                            <View key={c.key} style={{ flexDirection: 'row' }}>
                                <Text style={[s.cell, { width: catW }]}>{t[c.key]?.rev ? fNum(t[c.key].rev, 1) : ''}</Text>
                                <Text style={[s.cell, { width: catW }]}>{t[c.key]?.cost ? fNum(t[c.key].cost, 1) : ''}</Text>
                            </View>
                        ))}
                        <Text style={[s.cell, { width: totW, fontWeight: 600 }]}>{rowRev ? fNum(rowRev, 1) : ''}</Text>
                        <Text style={[s.cell, { width: totW, fontWeight: 600 }]}>{rowCost ? fNum(rowCost, 1) : ''}</Text>
                    </View>
                );
            })}

            {/* Totals section */}
            <View style={{ flexDirection: 'row' }}>
                <Text style={[s.cellLeft, { width: tkW, fontWeight: 700, backgroundColor: '#f1f5f9' }]}>Total</Text>
                <Text style={[s.cellLeft, { width: stW, backgroundColor: '#f1f5f9' }]}></Text>
                <Text style={[s.cellLeft, { width: dtW, backgroundColor: '#f1f5f9' }]}></Text>
                {CATEGORIES.map(c => (
                    <View key={c.key} style={{ flexDirection: 'row' }}>
                        <Text style={[s.cell, { width: catW, fontWeight: 700, backgroundColor: '#f1f5f9' }]}>{totals[c.key].rev ? fNum(totals[c.key].rev, 1) : ''}</Text>
                        <Text style={[s.cell, { width: catW, fontWeight: 700, backgroundColor: '#f1f5f9' }]}>{totals[c.key].cost ? fNum(totals[c.key].cost, 1) : ''}</Text>
                    </View>
                ))}
                <Text style={[s.cell, { width: totW, fontWeight: 700, backgroundColor: '#f1f5f9' }]}>{grandRev ? fNum(grandRev, 1) : ''}</Text>
                <Text style={[s.cell, { width: totW, fontWeight: 700, backgroundColor: '#f1f5f9' }]}>{grandCost ? fNum(grandCost, 1) : ''}</Text>
            </View>
        </View>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const TimeMaterialsReport = ({ refSubNav }) => {
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [jobs] = useAtom(dataSourceJobsAtom);

    const [costRows, setCostRows] = useState([]);
    const [billingRows, setBillingRows] = useState([]);
    const [billablePhases, setBillablePhases] = useState({});
    const [waiting, setWaiting] = useState(false);
    const rolling9Ref = useRef(null);
    const [activeView, setActiveView] = useState('weekly');

    const periods = useMemo(() => computePeriods(moment()), []);

    // Pull only for the currently-selected job. Team-wide loads were timing
    // out against the live (un-versioned) views.
    useEffect(() => {
        if (!selectedJob) {
            setCostRows([]);
            setBillingRows([]);
            setBillablePhases({});
            return;
        }
        const ids = [selectedJob];
        // Window the cost history fetch — large jobs hit the 3min SQL request
        // timeout at 13 months. The Weekly Trending view only goes back to
        // Last Month (≈5 weeks), so 3 months is more than enough and the
        // query returns in seconds even for $4M+ jobs.
        const costStartDate = moment().subtract(3, 'months').format('YYYY-MM-DD');
        setWaiting(true);
        Promise.all([
            loadJobCostHistoryLive({ ids, startDate: costStartDate }),
            loadARServiceBillingConsolidated({ ids }),
            loadBillablePhases({ ids })
        ]).then(([costRes, billRes, bpRes]) => {
            // Surface the raw response shape — handlers return {error: ...} on
            // SQL failure with 200 status, which the .data || [] dance swallows.
            if (costRes?.error || !costRes?.data) {
                console.warn('[T&M Summary] readJobCostHistoryLive returned no data:', costRes);
            }
            if (billRes?.error || !billRes?.data) {
                console.warn('[T&M Summary] readARServiceBillingConsolidated returned no data:', billRes);
            }
            const cost = costRes?.data || [];
            const bill = billRes?.data || [];
            const bpData = bpRes?.data || [];
            setCostRows(cost);
            setBillingRows(bill);
            const bp = {};
            bpData.forEach(d => {
                if (!d?.Job_Number || !d?.Phase_Code) return;
                bp[d.Job_Number] = bp[d.Job_Number] || {};
                bp[d.Job_Number][d.Phase_Code] = true;
            });
            setBillablePhases(bp);

            // --- debug: window boundaries + row counts + sample rows ---
            const fmt = (m) => m ? m.format('YYYY-MM-DD') : null;
            console.group(`[T&M Summary debug] job ${selectedJob}`);
            console.log('periods', Object.fromEntries(
                Object.entries(periods).map(([k, p]) => [k, `${fmt(p.start)} → ${fmt(p.end)}`])
            ));
            console.log('costRows', cost.length, 'sample:', cost.slice(0, 3));
            console.log('billingRows', bill.length, 'sample:', bill.slice(0, 3));
            console.log('billablePhases for this job:', bp[selectedJob] || {});
            const costJobCostSum = cost.reduce((s, r) => s + pNum(r.Job_Cost), 0);
            const billAmtSum = bill.reduce((s, r) => s + (pNum(r.Amount) || pNum(r.Invoice_Amount) || pNum(r.Revenue_Amount) || pNum(r.Extended_Price_Amount)), 0);
            console.log('totals — cost Job_Cost:', costJobCostSum.toFixed(2), '| billing Amount:', billAmtSum.toFixed(2));
            const costPhases = [...new Set(cost.map(r => r.Phase_Code).filter(Boolean))];
            const bpPhases = Object.keys(bp[selectedJob] || {});
            console.log('cost phases:', costPhases);
            console.log('billable phases (T&M):', bpPhases);
            console.log('cost phases that are BILLABLE:', costPhases.filter(p => bp[selectedJob]?.[p]));
            console.log('cost phases that are NON-BILLABLE:', costPhases.filter(p => !bp[selectedJob]?.[p]));
            console.groupEnd();
        }).catch(err => {
            console.error('T&M new view fetch failed', err);
        }).finally(() => setWaiting(false));
    }, [selectedJob, periods]);

    const selectedJobMeta = useMemo(
        () => (jobs || []).find(j => j.BU_ID === selectedJob) || null,
        [jobs, selectedJob]
    );

    // ----- aggregations -----
    // Weekly Trending periods: Last Week, 2 Weeks Ago, 3 Weeks Ago, Last Month, JTD
    // JTD uses the same 13-month cost window pulled on data load.
    const weeklyPeriods = {
        lastWeek: periods.lastWeek,
        twoWeeks: periods.twoWeeks,
        threeWeeks: periods.threeWeeks,
        lastMonth: periods.lastMonth,
        jtd: periods.jtd
    };
    const weeklyKeys = Object.keys(weeklyPeriods);
    const weeklyLabels = weeklyKeys.map(k => weeklyPeriods[k].label);

    // Weekly Trending: split cost & rev into Billable / Non-Billable / Total
    // using the billable-phase lookup on both sides (cost rows by Phase_Code,
    // billing rows by Phase_Code as well — both live in v_AR_Service_Billing).
    const trendingCostBillable = useMemo(
        () => bucketCosts(costRows, weeklyPeriods, billablePhases, (isBillable) => isBillable),
        [costRows, billablePhases]
    );
    const trendingCostNonBillable = useMemo(
        () => bucketCosts(costRows, weeklyPeriods, billablePhases, (isBillable) => !isBillable),
        [costRows, billablePhases]
    );
    const trendingRevBillable = useMemo(
        () => bucketBillings(billingRows, weeklyPeriods, (row) => !!billablePhases?.[getBillingJobNumber(row)]?.[row?.Phase_Code]),
        [billingRows, billablePhases]
    );
    const trendingRevNonBillable = useMemo(
        () => bucketBillings(billingRows, weeklyPeriods, (row) => !billablePhases?.[getBillingJobNumber(row)]?.[row?.Phase_Code]),
        [billingRows, billablePhases]
    );
    const trendingCostTotal = useMemo(
        () => sumPeriodCatMaps(trendingCostBillable, trendingCostNonBillable),
        [trendingCostBillable, trendingCostNonBillable]
    );
    const trendingRevTotal = useMemo(
        () => sumPeriodCatMaps(trendingRevBillable, trendingRevNonBillable),
        [trendingRevBillable, trendingRevNonBillable]
    );
    const ticketsBillable = useMemo(
        () => bucketByTicket(costRows, billingRows, weeklyPeriods, billablePhases, 'billable'),
        [costRows, billingRows, billablePhases]
    );
    const ticketsNonBillable = useMemo(
        () => bucketByTicket(costRows, billingRows, weeklyPeriods, billablePhases, 'nonBillable'),
        [costRows, billingRows, billablePhases]
    );
    const ticketsTotal = useMemo(
        () => bucketByTicket(costRows, billingRows, weeklyPeriods, billablePhases, 'total'),
        [costRows, billingRows, billablePhases]
    );
    const classesBillable = useMemo(
        () => bucketByClassification(costRows, weeklyPeriods, billablePhases, 'billable'),
        [costRows, billablePhases]
    );
    const classesNonBillable = useMemo(
        () => bucketByClassification(costRows, weeklyPeriods, billablePhases, 'nonBillable'),
        [costRows, billablePhases]
    );
    const classesTotal = useMemo(
        () => bucketByClassification(costRows, weeklyPeriods, billablePhases, 'total'),
        [costRows, billablePhases]
    );
    const leftToBill = useMemo(
        () => reconcileTickets(costRows, billingRows, billablePhases),
        [costRows, billingRows, billablePhases]
    );

    // Distinct Phase_Codes that appear in cost history, with their billable
    // classification per readBillablePhases (ftms3.Jobs_Phase_Type = 'T&M').
    const phaseBreakdown = useMemo(() => {
        const map = new Map();
        (costRows || []).forEach(row => {
            const phase = row.Phase_Code;
            if (!phase) return;
            const key = `${row.Job_Number}|${phase}`;
            const existing = map.get(key) || {
                job: row.Job_Number,
                phase,
                description: row.Phase_Code_Description || '',
                billable: !!billablePhases?.[row.Job_Number]?.[phase],
                totalCost: 0,
                lineCount: 0
            };
            existing.totalCost += pNum(row.Job_Cost);
            existing.lineCount += pNum(row.Line_Count) || 1;
            if (!existing.description && row.Phase_Code_Description) existing.description = row.Phase_Code_Description;
            map.set(key, existing);
        });
        return Array.from(map.values()).sort((a, b) => b.totalCost - a.totalCost);
    }, [costRows, billablePhases]);

    // ----- export -----
    const exportToExcel = () => {
        try {
            const wb = xlsx.utils.book_new();
            const buildAnalysisAOA = (revBy, costBy, pKeys, pLabels) => {
                const header1 = ['', ...pLabels.flatMap(l => [l, '', '', ''])];
                const header2 = ['Category', ...pLabels.flatMap(() => ['Rev', 'Cost', 'Profit', 'Margin'])];
                const rowFor = (key, label) => {
                    const row = [label];
                    pKeys.forEach(pk => {
                        const rev = revBy?.[pk]?.[key] ?? 0;
                        const cost = costBy?.[pk]?.[key] ?? 0;
                        const { profit, m } = margin(rev, cost);
                        row.push(rev, cost, profit, rev ? +(m.toFixed(2)) : '');
                    });
                    return row;
                };
                const data = [
                    header1,
                    header2,
                    ...CATEGORIES.map(c => rowFor(c.key, c.label)),
                    rowFor('total', 'Total')
                ];
                return data;
            };

            xlsx.utils.book_append_sheet(
                wb,
                xlsx.utils.aoa_to_sheet([
                    ['Weekly Trending'], [],
                    ['Non-Billable'], [],
                    ...buildAnalysisAOA(trendingRevNonBillable, trendingCostNonBillable, weeklyKeys, weeklyLabels),
                    [], ['Billable'], [],
                    ...buildAnalysisAOA(trendingRevBillable, trendingCostBillable, weeklyKeys, weeklyLabels),
                    [], ['Total'], [],
                    ...buildAnalysisAOA(trendingRevTotal, trendingCostTotal, weeklyKeys, weeklyLabels)
                ]),
                'T&M Analysis-Weekly Trending'
            );

            // Left to Bill — per-ticket × LLEEMMSSOO (Rev/Cost pairs) + totals
            const ltbHeader1 = ['', '', '', ...CATEGORIES.flatMap(c => [c.label, '']), 'Total', ''];
            const ltbHeader2 = ['Ticket #', 'Ticket Status', 'Date', ...CATEGORIES.flatMap(() => ['Rev', 'Cost']), 'Rev', 'Cost'];
            const ltbBody = (leftToBill || []).map(t => {
                let rowRev = 0;
                let rowCost = 0;
                const cells = [t.ticket, t.status || '', t.latestDate ? t.latestDate.format('YYYY-MM-DD') : ''];
                CATEGORIES.forEach(c => {
                    const rv = t[c.key]?.rev || 0;
                    const cv = t[c.key]?.cost || 0;
                    rowRev += rv;
                    rowCost += cv;
                    cells.push(rv, cv);
                });
                cells.push(rowRev, rowCost);
                return cells;
            });
            const totals = { labor: { rev: 0, cost: 0 }, equipment: { rev: 0, cost: 0 }, materials: { rev: 0, cost: 0 }, subs: { rev: 0, cost: 0 }, other: { rev: 0, cost: 0 } };
            let grandRev = 0;
            let grandCost = 0;
            (leftToBill || []).forEach(t => {
                CATEGORIES.forEach(c => {
                    totals[c.key].rev += t[c.key]?.rev || 0;
                    totals[c.key].cost += t[c.key]?.cost || 0;
                    grandRev += t[c.key]?.rev || 0;
                    grandCost += t[c.key]?.cost || 0;
                });
            });
            const ltbTotalRow = ['Total', '', '', ...CATEGORIES.flatMap(c => [totals[c.key].rev, totals[c.key].cost]), grandRev, grandCost];
            const ltbAOA = [
                ['Left to Bill — by Ticket'],
                ['Cost columns: tickets with cost but no matching billing. Rev columns: tickets with billing but no matching cost.'],
                [],
                ltbHeader1,
                ltbHeader2,
                ...ltbBody,
                ltbTotalRow
            ];
            xlsx.utils.book_append_sheet(wb, xlsx.utils.aoa_to_sheet(ltbAOA), 'T&M Analysis-Left to Bill');

            xlsx.utils.book_append_sheet(
                wb,
                xlsx.utils.aoa_to_sheet([['Rate Sheet Analysis'], [], ['Rate Sheets Selection']]),
                'Rate Sheet Analysis'
            );

            // Raw-data sheets for audit
            if (billingRows.length) {
                const billCols = Object.keys(billingRows[0]);
                xlsx.utils.book_append_sheet(
                    wb,
                    xlsx.utils.aoa_to_sheet([billCols, ...billingRows.map(r => billCols.map(c => r[c]))]),
                    'AR Service Billing Detail'
                );
            }
            if (costRows.length) {
                const costCols = Object.keys(costRows[0]);
                xlsx.utils.book_append_sheet(
                    wb,
                    xlsx.utils.aoa_to_sheet([costCols, ...costRows.map(r => costCols.map(c => r[c]))]),
                    'Job Cost History'
                );
            }

            const out = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `TandMSummary-${moment().format('YYYY.MM.DD')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error('export failed', err);
        }
    };

    // ----- render -----
    const views = [
        { key: 'weekly', label: 'Weekly Trending' },
        { key: 'leftToBill', label: 'Left to Bill' },
        { key: 'rolling9', label: 'Rolling 9' }
    ];

    return (
        <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', padding: 12, gap: 8, flexWrap: 'wrap' }}>
                {views.map(v => (
                    <Pressable key={v.key} style={s.viewBtn(activeView === v.key)} onPress={() => setActiveView(v.key)}>
                        <Text style={{ fontSize: 12, color: '#0f172a' }}>{v.label}</Text>
                    </Pressable>
                ))}
                <Pressable
                    style={{ marginLeft: 'auto', padding: 10, border: '1px solid #cbd5e1', borderRadius: 4, flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc' }}
                    onPress={() => {
                        if (activeView === 'rolling9') {
                            rolling9Ref.current?.exportToExcel?.();
                        } else {
                            exportToExcel();
                        }
                    }}
                >
                    <Icon name="↓" size={16} color="#0f172a" />
                    <Text style={{ marginLeft: 6, fontSize: 12 }}>Export</Text>
                    {waiting ? (
                        <View style={{ marginLeft: 8 }}>
                            <ActivityIndicator size={12} color="#0b142e" />
                        </View>
                    ) : null}
                </Pressable>
            </View>

            {activeView === 'rolling9' ? (
                <TimeMaterialsReportRolling9 ref={rolling9Ref} />
            ) : (
            <View style={{ margin: 12, padding: 12, backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                {!selectedJob && (
                    <Text style={{ fontSize: 13, color: '#64748b' }}>Select a job from the side menu to load T&M data.</Text>
                )}
                {selectedJob && activeView === 'weekly' && (
                    <View>
                        <Text style={s.sectionTitle}>Non-Billable</Text>
                        <AnalysisTable
                            periodKeys={weeklyKeys}
                            periodLabels={weeklyLabels}
                            revByPeriod={trendingRevNonBillable}
                            costByPeriod={trendingCostNonBillable}
                            ticketsByCategory={ticketsNonBillable}
                            classesByCategory={classesNonBillable}
                        />

                        <Text style={[s.sectionTitle, { marginTop: 24 }]}>Billable</Text>
                        <AnalysisTable
                            periodKeys={weeklyKeys}
                            periodLabels={weeklyLabels}
                            revByPeriod={trendingRevBillable}
                            costByPeriod={trendingCostBillable}
                            ticketsByCategory={ticketsBillable}
                            classesByCategory={classesBillable}
                        />

                        <Text style={[s.sectionTitle, { marginTop: 24 }]}>Total</Text>
                        <AnalysisTable
                            periodKeys={weeklyKeys}
                            periodLabels={weeklyLabels}
                            revByPeriod={trendingRevTotal}
                            costByPeriod={trendingCostTotal}
                            ticketsByCategory={ticketsTotal}
                            classesByCategory={classesTotal}
                        />

                        <Text style={[s.sectionTitle, { marginTop: 24 }]}>Phase Codes in Cost History</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                            All Phase_Codes that appear on cost rows, with their billable classification per
                            readBillablePhases (ftms3.Jobs_Phase_Type = 'T&M'). Sorted by total cost desc.
                        </Text>
                        <View>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={[s.head, { width: 110 }]}>Phase Code</Text>
                                <Text style={[s.head, { width: 320 }]}>Description</Text>
                                <Text style={[s.head, { width: 110 }]}>Billable?</Text>
                                <Text style={[s.head, { width: 130 }]}>Total Cost</Text>
                                <Text style={[s.head, { width: 90 }]}>Lines</Text>
                            </View>
                            {phaseBreakdown.length === 0 ? (
                                <View style={{ flexDirection: 'row' }}>
                                    <Text style={[s.cellLeft, { width: 760, fontStyle: 'italic', color: '#94a3b8' }]}>
                                        No cost rows loaded yet.
                                    </Text>
                                </View>
                            ) : phaseBreakdown.map((p) => (
                                <View key={`${p.job}|${p.phase}`} style={{ flexDirection: 'row' }}>
                                    <Text style={[s.cellLeft, { width: 110 }]}>{p.phase}</Text>
                                    <Text style={[s.cellLeft, { width: 320 }]}>{p.description}</Text>
                                    <Text style={[
                                        s.cell,
                                        {
                                            width: 110,
                                            textAlign: 'center',
                                            backgroundColor: p.billable ? '#dcfce7' : '#fee2e2',
                                            color: p.billable ? '#166534' : '#991b1b',
                                            fontWeight: 600
                                        }
                                    ]}>{p.billable ? 'Billable' : 'Non-Billable'}</Text>
                                    <Text style={[s.cell, { width: 130 }]}>{p.totalCost ? fNum(p.totalCost, 1) : ''}</Text>
                                    <Text style={[s.cell, { width: 90 }]}>{p.lineCount || ''}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                )}
                {selectedJob && activeView === 'leftToBill' && (
                    <ReconcileTable tickets={leftToBill} />
                )}
            </View>
            )}
        </View>
    );
};

export default TimeMaterialsReport;
